import React, { useEffect, useState } from 'react';
import {
    Modal, Form, Input, Button, Space, Select, Switch, message, Flex,
    Typography, Menu, Row, Col, Card, Checkbox
} from 'antd';
import {
    PlusOutlined, MinusCircleOutlined, EditOutlined, SaveOutlined,
    ShopOutlined, ApiOutlined, SettingOutlined
} from '@ant-design/icons';
import axiosApi from '../../axiosApi/axiosApi';

const { Title, Text } = Typography;
const { Option } = Select;

const SettingModal = ({ visible, onClose }) => {
    const [storeForm] = Form.useForm();
    const [webhookForm] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('store');
    const [editingStore, setEditingStore] = useState(false);
    const [storeData, setStoreData] = useState({});
    const [selectedWebhookTopics, setSelectedWebhookTopics] = useState([]);

    const menuItems = [
        { key: 'store', icon: <ShopOutlined />, label: 'Store Information' },
        { key: 'webhook', icon: <ApiOutlined />, label: 'Webhook Management' },
        { key: 'Ghl Account', icon: <SettingOutlined />, label: 'GHL Account' },
    ];

    const topics = ['order.created', 'product.created'];

    useEffect(() => {
        if (visible || true) fetchStoreInfo();
    }, [visible]);

    async function fetchStoreInfo() {
        try {
            const { data } = await axiosApi.get(`/vendor/info`);
            setStoreData(data);
            setSelectedWebhookTopics(data.webhook || []);
            storeForm.setFieldsValue({
                storeName: data.storeName,
                email: data.email,
                password: '',
                storeUrl: data.storeUrl,
                role: data.role,
                wooConsumerKey: data.wooConsumerKey,
                wooConsumerSecret: data.wooConsumerSecret,
                approved: data.approved,
                
            });
            webhookForm.setFieldsValue({
                webhookTopics: data.webhook || []
            });
        } catch (error) {
            message.error('Failed to fetch store information');
        }
    }

    const onStoreInfoSubmit = async (values) => {
        setLoading(true);
        try {
            await axiosApi.post(`/vendor/edit`, values);
            message.success('Store information updated');
            setEditingStore(false);
            fetchStoreInfo();
        } catch {
            message.error('Update failed');
        } finally {
            setLoading(false);
        }
    };

    const onWebhookSubmit = async () => {
        setLoading(true);
        try {
            await axiosApi.post(`/vendor/edit`, {
                ...storeData,
                webhook: selectedWebhookTopics
            });
            message.success('Webhook configuration saved');
            fetchStoreInfo();
        } catch {
            message.error('Webhook update failed');
        } finally {
            setLoading(false);
        }
    };
     
    const onGhlSubmit = async (values) => {
        setLoading(true);
        try {
            await axiosApi.post(`/vendor/edit`, {
                ...storeData,
                ...values // includes ghlClientId and ghlClientSecret
            });
            message.success('GHL account info saved');
            fetchStoreInfo();
        } catch {
            message.error('Failed to save GHL info');
        } finally {
            setLoading(false);
        }
    };
    const handleModalClose = () => {
        setEditingStore(false);
        setActiveTab('store');
        onClose();
    };

    const generateWebhookUrl = (topic) => {
        if (!storeData.storeUrl) return '';
        return `https://gearvana.com/api/webhooks/${topic}`;
    };
    

    const renderStoreInfo = () => (
        <div style={{ padding: '16px 0' }}>
            {!editingStore ? (
                <Card
                    title="Store Information"
                    extra={<Button style={{background: "#1677ff", color: "white"}} icon={<EditOutlined />} onClick={() => setEditingStore(true)}>Edit Store Info</Button>}
                    style={{ border: 'none', boxShadow: 'none' }}
                >
                    <Row gutter={[16, 26]}>
                        {[
                            ['Store Name', storeData.storeName],
                            ['Email', storeData.email],
                            ['Store URL', storeData.storeUrl],
                            ['Role', storeData.role],
                            ['Consumer Key', storeData.wooConsumerKey],
                            ['Consumer Secret', storeData.wooConsumerSecret ? '*'.repeat(storeData.wooConsumerSecret.length) : 'Not set'],
                        ].map(([label, value], idx) => (
                            <Col span={16} key={idx}>
                                <Text strong>{label} :</Text><br />
                                <Text>{value || 'Not set'}</Text>
                            </Col>
                        ))}
                        <Col span={12}>
                            <Text strong>Approved:</Text><br />
                            <Switch checked={storeData.approved} disabled size="small" />
                        </Col>
                    </Row>
                </Card>
            ) : (
                <Card title="Edit Store Information">
                    <Form form={storeForm} layout="vertical" onFinish={onStoreInfoSubmit}>
                        <Row gutter={16}>
                            {[
                                ['storeName', 'Store Name', <Input />, true],
                                ['email', 'Email', <Input />, true],
                                // ['password', 'Password', <Input.Password placeholder="Leave blank to keep current" />],
                                ['storeUrl', 'Store URL', <Input />, true],
                                ['role', 'Role', <Select>
                                    <Option value="vendor">Vendor</Option>
                                    <Option value="admin">Admin</Option>
                                </Select>, true],
                                ['approved', 'Approved', <Switch />, false, 'checked'],
                                ['wooConsumerKey', 'WooCommerce Consumer Key', <Input />, true],
                                ['wooConsumerSecret', 'WooCommerce Consumer Secret', <Input.Password />, true],
                            ].map(([name, label, input, required, valueProp], idx) => (
                                <Col span={16} key={idx}>
                                    <Form.Item
                                        name={name}
                                        label={label}
                                        rules={required ? [{ required: true }] : []}
                                        valuePropName={valueProp}
                                    >
                                        {input}
                                    </Form.Item>
                                </Col>
                            ))}
                        </Row>
                        <Form.Item>
                            <Flex justify="flex-end" gap="small">
                                <Button style={{background: "#1677ff", color: "white"}} onClick={() => setEditingStore(false)}>Cancel</Button>
                                <Button type="primary" style={{background: "#1677ff", color: "white"}} htmlType="submit" loading={loading} icon={<SaveOutlined />}>
                                    Save Changes
                                </Button>
                            </Flex>
                        </Form.Item>
                    </Form>
                </Card>
            )}
        </div>
    );

    const renderWebhookManagement = () => (
        <div style={{ padding: '16px 0' }}>
            <Card title="Webhook Management">
                <Form form={webhookForm} layout="vertical" onFinish={onWebhookSubmit}>
                    <Form.Item
                        name="webhookTopics"
                        label="Select Webhook Topics"
                        rules={[{ required: true, message: 'Please select at least one topic' }]}
                    >
                        <Checkbox.Group
                            options={topics.map(t => ({
                                label: (
                                    <div>
                                        <Text strong>{t.replace('.', ' ')}</Text><br />
                                        <Text type="secondary" style={{ fontSize: 12 }}>
                                            Triggers when {t.split('.')[1]} is {t.split('.')[0]}
                                        </Text>
                                    </div>
                                ),
                                value: t
                            }))}
                            value={selectedWebhookTopics}
                            onChange={setSelectedWebhookTopics}
                        />
                    </Form.Item>

                    {selectedWebhookTopics.length > 0 && (
                        <>
                            <Card size="small" title="Generated Webhook URLs" style={{ marginBottom: 16 }}>
                                {selectedWebhookTopics.map(topic => (
                                    <div key={topic} style={{ marginBottom: 12 }}>
                                        <Text strong>{topic}:</Text>
                                        <Input.TextArea
                                            value={generateWebhookUrl(topic)}
                                            readOnly
                                            rows={1}
                                            style={{ fontSize: '12px', fontFamily: 'monospace', marginTop: 4 }}
                                        />
                                    </div>
                                ))}
                            </Card>
                          
                            {/* <Card size="small" title="Webhook Secret">
                                <Text strong>Secret:</Text>
                                <Input value={storeData?.webhookSecret} readOnly style={{ fontFamily: 'monospace', fontSize: 12 }} />
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                    Use this secret when configuring your WooCommerce webhook
                                </Text>
                            </Card> */}
                        </>
                    )}

                    <Form.Item style={{ marginTop: 24 }}>
                        <Flex justify="flex-end">
                            <Button
                                type="primary"
                                style={{background: "#1677ff", color: "white"}}
                                htmlType="submit"
                                loading={loading}
                                icon={<SaveOutlined />}
                                disabled={selectedWebhookTopics.length === 0}
                            >
                                Save Webhook Configuration
                            </Button>
                        </Flex>
                    </Form.Item>
                </Form>
            </Card>
        </div>
    );

    const renderGhlAcccountInfo = () => (
        <div style={{ padding: '16px 0' }}>
            <Card title="GHL Account Integration">
                <Form form={storeForm} layout="vertical" onFinish={onGhlSubmit}>
                    <Row gutter={16}>
                        <Col span={16}>
                            <Form.Item
                                name="ghlClientId"
                                label="GHL Client ID"
                                rules={[{ required: true, message: 'Please enter your GHL Client ID' }]}
                            >
                                <Input placeholder="Enter GHL Client ID" />
                            </Form.Item>
                        </Col>
                        <Col span={16}>
                            <Form.Item
                                name="ghlClientSecret"
                                label="GHL Client Secret"
                                rules={[{ required: true, message: 'Please enter your GHL Client Secret' }]}
                            >
                                <Input.Password placeholder="Enter GHL Client Secret" />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Form.Item>
                        <Flex justify="flex-end">
                            <Button
                                type="primary"
                                htmlType="submit"
                                style={{ background: "#1677ff", color: "white" }}
                                icon={<SaveOutlined />}
                                loading={loading}
                            >
                                Save GHL Settings
                            </Button>
                        </Flex>
                    </Form.Item>
                </Form>
            </Card>
        </div>
    );
    
    return (
        <Modal
            title={<Space><SettingOutlined /> Settings</Space>}
            open={visible}
            onCancel={()=>onClose(false)}
            footer={null}
            width={900}
            // style={{ top: 20 }}
        >
            <Row gutter={24}>
                <Col xs={24} sm={24} md={8} style={{ marginBottom: 16 }}>
                    <Menu
                        mode="vertical"
                        selectedKeys={[activeTab]}
                        items={menuItems}
                        onClick={({ key }) => setActiveTab(key)}
                        style={{
                            backgroundColor: '#f5f5f5',
                            borderRadius: 8,
                            padding: 8,
                        }}
                    />
                </Col>
                <Col xs={24} sm={24} md={16}>
                    {activeTab === 'store' && renderStoreInfo()}
                    {activeTab === 'webhook' && renderWebhookManagement()}
                    {storeData.role === "Super Admin" ? activeTab === 'Ghl Account' && renderGhlAcccountInfo():null}
                </Col>
            </Row>
        </Modal>
    );
};

export default SettingModal;
